import React, {
  useState,
  useRef,
  useEffect,
  MouseEvent,
  FocusEvent,
} from "react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { DTValue } from "@/types";
import useOnclickOutside from "react-cool-onclickoutside";
import "react-datepicker/dist/react-datepicker.css";
import "./style.scss";

interface PresetTime {
  name: string;
  getTime: () => Date | undefined;
}
interface DateTimePickerProps {
  isDisabled?: boolean;
  offsetTo?: "right" | "left";
  isBefore?: boolean;
  startTimeDisplay?: string;
  endTimeDisplay?: string;
  value: DTValue;
  onChange: (value: DTValue) => void;
}

const commonPresetTimes = [
  {
    name: "now",
    getTime: () => new Date(),
  },
  {
    name: "1 minute ago",
    getTime: () => new Date(Date.now() - 60 * 1000),
  },
  {
    name: "1 hour ago",
    getTime: () => new Date(Date.now() - 60 * 60 * 1000),
  },
  {
    name: "2 hours ago",
    getTime: () => new Date(Date.now() - 2 * 60 * 60 * 1000),
  },
  {
    name: "12 hours ago",
    getTime: () => new Date(Date.now() - 12 * 60 * 60 * 1000),
  },
  {
    name: "1 day ago",
    getTime: () => new Date(Date.now() - 24 * 60 * 60 * 1000),
  },
  {
    name: "5 days ago",
    getTime: () => new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
  },
  {
    name: "1 week ago",
    getTime: () => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
  },
  {
    name: "1 month ago",
    getTime: () => new Date(new Date().setMonth(new Date().getMonth() - 1)),
  },
  {
    name: "3 months ago",
    getTime: () => new Date(new Date().setMonth(new Date().getMonth() - 3)),
  },
  {
    name: "6 months ago",
    getTime: () => new Date(new Date().setMonth(new Date().getMonth() - 6)),
  },
  {
    name: "1 year ago",
    getTime: () =>
      new Date(new Date().setFullYear(new Date().getFullYear() - 1)),
  },
  {
    name: "2 years ago",
    getTime: () =>
      new Date(new Date().setFullYear(new Date().getFullYear() - 2)),
  },
  {
    name: "Earlist Possible",
    getTime: () => new Date("2022-01-02"),
  },
];

const formatTimeDisplay = (time: Date) =>
  format(new Date(time), "MMM dd, yyyy hh:mm aa");

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  isDisabled,
  offsetTo = "right",
  isBefore = false,
  value,
  onChange,
}) => {
  const [isClosed, setIsClosed] = useState<boolean>(true);
  const [isRelative, setIsRelative] = useState<boolean>(true);
  const [selectedTime, setSelectedTime] = useState<Date | undefined>(
    new Date(),
  );
  const panelRef = useRef<HTMLDivElement>(null);
  const ref = useOnclickOutside(() => {
    setIsClosed(true);
  });

  useEffect(() => {
    if (!isClosed) {
      panelRef.current?.focus();
    }
  }, [isClosed]);

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    setIsClosed(!isClosed);
  };
  const handleOnBlur = (e: FocusEvent<HTMLDivElement>) => {
    // find if it is child component or not
    if (e.relatedTarget === null) {
      setIsClosed(true);
    }
  };

  // handle all kinds of time
  const handleTab = (status: boolean) => {
    setIsRelative(() => status);
  };
  const handlePille = (presetTime: PresetTime) => {
    setSelectedTime(presetTime.getTime());
    // handleTimeCB(presetTime.getTime());
    onChange({
      preset: presetTime.name,
      time: undefined,
    });
    setIsClosed(true);
  };
  const handleDateAndTime = (time: Date) => {
    setSelectedTime(time);
    // handleTimeCB(time);
    onChange({
      preset: "",
      time,
    });
  };

  return (
    <div
      className="date-time-picker"
      style={{ borderColor: isDisabled ? "#df2f2b" : "#d4d4d4" }}
      ref={ref}
    >
      <div
        className={isDisabled ? "date-time-input disabled" : "date-time-input"}
        onClick={handleClick}
      >
        {value.preset ? value.preset : formatTimeDisplay(value.time!)}
      </div>
      {!isClosed && (
        <div
          className={`date-time-panel ${offsetTo}`}
          // tabIndex={-1}
          onBlur={handleOnBlur}
          // ref={panelRef}
        >
          <div className="tabs">
            <button
              className={isRelative ? "selected" : ""}
              onClick={() => handleTab(true)}
            >
              Relative
            </button>
            <button
              className={!isRelative ? "selected" : ""}
              onClick={() => handleTab(false)}
            >
              Absolute
            </button>
          </div>

          <div className="date-time-panel-wrapper">
            {isRelative ? (
              <div className="relative-tab">
                <div className="title">PRESET</div>
                <div className="pilles">
                  {commonPresetTimes
                    .filter((time) => !isBefore || time.name !== "now")
                    .map((presetTime: PresetTime) => (
                      <div
                        className="pille"
                        key={presetTime.name}
                        onClick={() => handlePille(presetTime)}
                      >
                        {presetTime.name}
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <div className="absolute-tab">
                <DatePicker
                  selected={selectedTime}
                  onChange={handleDateAndTime}
                  timeInputLabel="Input Specific Time: "
                  maxDate={new Date()}
                  showMonthDropdown
                  showTimeInput
                  showTimeSelect
                  inline
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateTimePicker;
